

import type { Athlete, TrackPoint } from '../types';
import { getDistance } from './geoUtils';

export interface LapCrossing {
    athleteId: string;
    athleteName: string;
    crossingTime: number; // timestamp in ms
}

const LAP_CROSSING_THRESHOLD_METERS = 25;

export interface Lap {
    lapNumber: number;
    lapTime: number; // in ms
    avgSpeed: number; // in km/h
    avgPower: number | null;
    avgHeartRate: number | null;
}

export interface OffsetAdjustment {
    timestamp: number;
    lapOffset: number;
    distanceOffset: number;
}

export interface RaceIncident {
    timestamp: number;
    type: 'CRASH' | 'MECHANICAL' | 'STOP';
    description: string;
}

// --- Helper: Course Geometry ---

export const calculateCourseLength = (path: [number, number][]): number => {
    let length = 0;
    for (let i = 1; i < path.length; i++) {
        length += getDistance(path[i - 1][0], path[i - 1][1], path[i][0], path[i][1]);
    }
    return length;
};

export const calculateCourseCumulativeDistances = (path: [number, number][]): number[] => {
    const cumulative: number[] = [0];
    let total = 0;
    for (let i = 1; i < path.length; i++) {
        total += getDistance(path[i - 1][0], path[i - 1][1], path[i][0], path[i][1]);
        cumulative.push(total);
    }
    return cumulative;
};

/**
 * Projects a position onto the course path and returns the cumulative distance from start.
 * Snaps to the closest vertex on the course path using a weighted search.
 * biases selection towards the 'hintDistance' to avoid snapping to the wrong leg on loops/out-and-back courses.
 */
export const getProjectedPathDistance = (
    lat: number,
    lon: number,
    path: [number, number][],
    cumulativeDistances: number[],
    courseLength: number,
    hintMetricDistance: number
): number => {
    if (path.length === 0) return 0;

    let bestScore = Infinity;
    let closestIndex = 0;
    const LINEAR_PENALTY_WEIGHT = 0.5;

    for (let i = 0; i < path.length; i++) {
        const pointDist = cumulativeDistances[i];
        let linearDiff = Math.abs(pointDist - hintMetricDistance);
        if (linearDiff > courseLength / 2) linearDiff = courseLength - linearDiff; // Handle loops

        const dLat = (path[i][0] - lat) * 111320;
        const dLon = (path[i][1] - lon) * 111320 * Math.cos(lat * Math.PI / 180);
        const physicalDist = Math.sqrt(dLat * dLat + dLon * dLon);

        const score = physicalDist + (linearDiff * LINEAR_PENALTY_WEIGHT);

        if (score < bestScore) {
            bestScore = score;
            closestIndex = i;
        }
    }
    return cumulativeDistances[closestIndex];
};

// --- Core Analysis ---

/**
 * Extracts a single start-to-start loop from a multi-lap track path.
 * Used as a fallback when course.path is empty in Firestore.
 */
export const extractSingleCircuitLoop = (fullTrackPath: [number, number][], startLine: [number, number]): [number, number][] => {
    if (!fullTrackPath || fullTrackPath.length < 10 || !startLine || (startLine[0] === 0 && startLine[1] === 0)) {
        return fullTrackPath || [];
    }

    const THRESHOLD_METERS = 35;
    const MIN_LOOP_POINTS = 20;
    
    let firstCrossingIndex = -1;
    let secondCrossingIndex = -1;

    // Find the first crossing point (start of lap 1)
    for (let i = 0; i < fullTrackPath.length; i++) {
        const dist = getDistance(fullTrackPath[i][0], fullTrackPath[i][1], startLine[0], startLine[1]);
        if (dist <= THRESHOLD_METERS) {
            firstCrossingIndex = i;
            break;
        }
    }

    if (firstCrossingIndex === -1) firstCrossingIndex = 0;

    // Find the second crossing point (end of lap 1) after leaving the start area
    let hasLeftStartArea = false;
    for (let i = firstCrossingIndex + 1; i < fullTrackPath.length; i++) {
        const dist = getDistance(fullTrackPath[i][0], fullTrackPath[i][1], startLine[0], startLine[1]);
        if (!hasLeftStartArea && dist > 150) {
            hasLeftStartArea = true;
        }
        if (hasLeftStartArea && dist <= THRESHOLD_METERS && (i - firstCrossingIndex) >= MIN_LOOP_POINTS) {
            secondCrossingIndex = i;
            break;
        }
    }

    if (secondCrossingIndex > firstCrossingIndex) {
        return fullTrackPath.slice(firstCrossingIndex, secondCrossingIndex + 1);
    }

    // Fallback: Return first 1/30th of the track as an estimated single lap
    const estimatedLoopLength = Math.max(20, Math.floor(fullTrackPath.length / 30));
    return fullTrackPath.slice(0, estimatedLoopLength);
};

export const getLapCountAtTime = (track: TrackPoint[], time: number, startLine: [number, number]): number => {
    if (track.length < 2 || !startLine || (startLine[0] === 0 && startLine[1] === 0) || time <= track[0].timestamp) {
        return 0;
    }

    const MIN_DISTANCE_FOR_LAP_METERS = 300; // Must travel at least 300m for a lap crossing
    const MIN_LAP_TIME_MS = 25000; // 25s minimum lap time debounce

    // Find the index of the point just after the current time
    let high = track.length - 1;
    let low = 0;
    let timeIndex = track.length;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (track[mid].timestamp >= time) {
            timeIndex = mid;
            high = mid - 1;
        } else {
            low = mid + 1;
        }
    }

    let lapCount = 0;
    let wasNearStart = getDistance(track[0].lat, track[0].lon, startLine[0], startLine[1]) < LAP_CROSSING_THRESHOLD_METERS;
    let lastCrossingTimestamp = track[0].timestamp;
    let lastCrossingDistance = track[0].distance;

    for (let i = 1; i < timeIndex; i++) {
        const point = track[i];
        const distanceToStart = getDistance(point.lat, point.lon, startLine[0], startLine[1]);
        const isNearStart = distanceToStart < LAP_CROSSING_THRESHOLD_METERS;

        if (isNearStart && !wasNearStart) {
            const timeSinceLastCrossing = point.timestamp - lastCrossingTimestamp;
            const distanceSinceLastCrossing = point.distance - lastCrossingDistance;

            // Enforce minimum travel distance and time debounce to prevent staging noise & double triggers
            if (distanceSinceLastCrossing >= MIN_DISTANCE_FOR_LAP_METERS && timeSinceLastCrossing >= MIN_LAP_TIME_MS) {
                lapCount++;
                lastCrossingTimestamp = point.timestamp;
                lastCrossingDistance = point.distance;
            }
        }
        wasNearStart = isNearStart;
    }
    return lapCount;
};

export const calculateLapsForAthlete = (track: TrackPoint[], startLine: [number, number]): Lap[] => {
    if (!track || track.length < 2 || !startLine || (startLine[0] === 0 && startLine[1] === 0)) return [];

    const laps: Lap[] = [];
    let lapCounter = 1;
    let lapStartTime = track[0].timestamp;
    let lapStartDistance = track[0].distance;
    
    let powerSum = 0;
    let powerCount = 0;
    let hrSum = 0;
    let hrCount = 0;

    let wasNearStart = true;

    for (let i = 1; i < track.length; i++) {
        const point = track[i];
        
        if (point.power !== null && point.power !== undefined) {
            powerSum += point.power;
            powerCount++;
        }
        if (point.heartRate !== null && point.heartRate !== undefined) {
            hrSum += point.heartRate;
            hrCount++;
        }

        const distanceToStart = getDistance(point.lat, point.lon, startLine[0], startLine[1]);
        const isNearStart = distanceToStart < LAP_CROSSING_THRESHOLD_METERS;

        if (isNearStart && !wasNearStart) {
            const lapTime = point.timestamp - lapStartTime;
            const lapDistance = point.distance - lapStartDistance;
            const avgSpeed = lapTime > 0 ? (lapDistance / (lapTime / 1000)) * 3.6 : 0;

            laps.push({
                lapNumber: lapCounter,
                lapTime,
                avgSpeed,
                avgPower: powerCount > 0 ? powerSum / powerCount : null,
                avgHeartRate: hrCount > 0 ? hrSum / hrCount : null
            });

            lapCounter++;
            lapStartTime = point.timestamp;
            lapStartDistance = point.distance;
            powerSum = 0;
            powerCount = 0;
            hrSum = 0;
            hrCount = 0;
        }
        wasNearStart = isNearStart;
    }

    return laps;
};

export const calculateRaceLaps = (_athletes: Athlete[], _startLine: [number, number]): Record<number, LapCrossing[]> => {
    console.warn("[raceAnalysis] calculateRaceLaps is deprecated. Logic moved to Cloud Function 'analyzeRaceStats'.");
    return {};
};

/**
 * Calculates the continuous 'Virtual Race Distance' for an athlete.
 */
export const calculateVirtualRaceDistance = (
    lapsCompleted: number,
    projectedPathDistance: number,
    courseLength: number,
    _totalRecordedDistance?: number
): number => {
    // Virtual distance = (Laps * CourseLength) + ProgressOnCurrentLap
    return (lapsCompleted * courseLength) + projectedPathDistance;
};

// NOTE: calculateDynamicOffsets has been moved to Backend (Firebase Cloud Functions)
// to protect proprietary algorithms.
// Please see cloudAnalysisService.ts